import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import type { DispatchTableRow } from "./dispatchTableRow";
import { openTaskOfRow, type TaskFold } from "./tasks";

/** One task entry a right-click menu offers: make a task, or move the one already in force. */
export type TaskMenuAction = { kind: "make" } | { kind: "move"; task: OpenTask };

/**
 * The task entries a right-click menu offers for one target.
 *
 * "Make a task…" whenever a task can name anything there, the target or its driver; then "Move task…" when an open
 * task is found on it. A target with neither is offered nothing.
 *
 * @param nameable - Whether a task can name the target or its driver at all.
 * @param current - The open task found on the target, or null when there is none.
 */
export function taskMenuActionsFor(nameable: boolean, current: OpenTask | null): readonly TaskMenuAction[] {
  const make: readonly TaskMenuAction[] = nameable ? [{ kind: "make" }] : [];
  if (current === null) return make;
  return [...make, { kind: "move", task: current }];
}

/**
 * The task entries a right-click on a table row offers: a driver row, a message row, or a task's own row on the shelf.
 *
 * Every row has a driver a task can name, so every row offers "Make a task…".
 *
 * @param row - The row right-clicked.
 * @param grain - The view's grain, which decides whether a row stands for its message or its driver.
 * @param fold - The workspace's open tasks, to find the one already on the row's subject.
 */
export function rowTaskMenuActions(
  row: DispatchTableRow,
  grain: DispatchGrain,
  fold: TaskFold,
): readonly TaskMenuAction[] {
  return taskMenuActionsFor(true, openTaskOfRow(row, grain, fold));
}

/** The text a task menu entry reads. */
export function taskMenuLabel(action: TaskMenuAction): string {
  switch (action.kind) {
    case "make":
      return "Make a task…";
    case "move":
      return "Move task…";
  }
}
